import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Building2, GitCompareArrows } from "lucide-react";
import { getCompanies } from "../lib/api";
import { CompanyCard } from "../components/CompanyCard";
import { PageTitle } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { DataState, EmptyState } from "../components/DataState";
import { SkeletonGrid } from "../components/LoadingSkeleton";

const CAP_FILTERS = [
  { key: "all", label: "All", testId: "companies-filter-all" },
  { key: "largecap", label: "Large Cap", testId: "discover-filter-largecap" },
  { key: "midcap", label: "Mid Cap", testId: "discover-filter-midcap" },
  { key: "smallcap", label: "Small Cap", testId: "discover-filter-smallcap" },
];

export default function Companies() {
  const [params, setParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const sector = params.get("sector") || undefined;
  const filter = params.get("filter") || undefined;
  const cap = params.get("cap") || (["largecap", "midcap", "smallcap"].includes(filter) ? filter : "all");

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (sector) q.sector = sector;
    if (filter && !["largecap", "midcap", "smallcap"].includes(filter)) q.filter = filter;
    if (cap && cap !== "all") q.cap = cap.replace("cap", "");
    setError(false);
    getCompanies(q).then((d) => { setCompanies(d.companies); setLoading(false); }).catch(() => { setError(true); setLoading(false); });
  }, [sector, filter, cap]);

  const setCap = (key) => {
    const next = new URLSearchParams(params);
    next.delete("filter");
    if (key === "all") next.delete("cap"); else next.set("cap", key);
    setParams(next);
  };

  return (
    <div className="space-y-6">
      <PageTitle
        kicker="Company Directory"
        title="Indian listed companies"
        subtitle="Browse the LONGVIEW universe of NSE/BSE companies. Open any company to understand the business, its financial health and its risks before you ever think about price."
        right={
          <div className="flex items-center gap-2">
            <Link to="/compare" data-testid="companies-compare-link" className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.1] bg-[#0B0C0E] px-3 py-1.5 font-mono text-xs text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200">
              <GitCompareArrows className="h-3.5 w-3.5" /> Compare
            </Link>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {CAP_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={f.testId}
            onClick={() => setCap(f.key)}
            className={`lv-press rounded-md border px-3 py-1.5 font-mono text-xs transition-colors ${
              (cap === f.key || (f.key === "all" && cap === "all"))
                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                : "border-white/[0.08] text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
        {sector && <span className="rounded-md border border-white/[0.08] px-3 py-1.5 font-mono text-xs text-zinc-400">Sector: {sector}</span>}
      </div>

      {error ? <DataState /> : loading ? (
        <SkeletonGrid count={8} />
      ) : companies.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No companies match this filter"
          detail="Try a different sector or market-cap filter — the current LONGVIEW coverage doesn't have a match yet."
        />
      ) : (
        <StaggerGrid className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {companies.map((c) => <StaggerItem key={c.ticker}><CompanyCard c={c} /></StaggerItem>)}
        </StaggerGrid>
      )}
    </div>
  );
}
